import type { DrawingKind } from "lightweight-charts-drawing"
import type { ReactNode } from "react"

// One icon per drawing tool (18 × 18, currentColor), for the favourites bar, as TradingView draws
// them. Tools without their own drawing use their family's (channels, pitchforks, Gann, …).

const Svg = ({ children }: { readonly children: ReactNode }) => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {children}
  </svg>
)

const dot = (x: number, y: number) => <circle cx={x} cy={y} r="1.6" />

const ICONS: Partial<Record<DrawingKind, ReactNode>> = {
  "trend-line": <>{dot(3.5, 14.5)}{dot(14.5, 3.5)}<path d="M4.6 13.4l8.8-8.8" /></>,
  ray: <>{dot(3.5, 14.5)}<path d="M4.6 13.4L16.5 1.5" /></>,
  "info-line": <>{dot(3, 15)}{dot(11, 7)}<path d="M4 14l6-6" /><rect x="11" y="10" width="5.5" height="5" rx="0.8" /></>,
  "extended-line": <>{dot(7, 11)}{dot(11, 7)}<path d="M1 17l5-5M8 10l2-2M12 6l5-5" /></>,
  "trend-angle": <>{dot(3, 14)}<path d="M4 13l10-9M4 14h11M10 14a6 6 0 0 0-1.5-4" /></>,
  "horizontal-line": <>{dot(9, 9)}<path d="M1.5 9h6M10.5 9h6" /></>,
  "horizontal-ray": <>{dot(4, 9)}<path d="M5.6 9h11" /></>,
  "vertical-line": <>{dot(9, 9)}<path d="M9 1.5v6M9 10.5v6" /></>,
  "cross-line": <path d="M9 1.5v6M9 10.5v6M1.5 9h6M10.5 9h6" />,
  "parallel-channel": <>{dot(3, 11)}{dot(13, 3)}{dot(5, 16)}<path d="M4.2 10l7.6-6.2M6.2 15l9.3-7.6" /></>,
  "regression-trend": <><path d="M2 12l14-7" /><path d="M2 8l14-7M2 16l14-7" strokeDasharray="1.5 1.5" /></>,
  pitchfork: <><path d="M2 9h5M7 4v10M7 4h9M7 9h9M7 14h9" />{dot(2, 9)}</>,
  "fib-retracement": <><path d="M2 3h14M2 7h14M2 11h14M2 15h14" /><path d="M3 15L15 3" strokeDasharray="1.5 1.5" /></>,
  "gann-box": <><rect x="2.5" y="2.5" width="13" height="13" /><path d="M2.5 9h13M9 2.5v13M2.5 15.5L15.5 2.5" /></>,
  "gann-fan": <><path d="M2 16L16 2M2 16l14-5M2 16l5-14M2 16l14 0" />{dot(2, 16)}</>,
  "xabcd-pattern": <><path d="M2 14l3-9 4 6 3-8 4 10" />{dot(2, 14)}{dot(16, 13)}</>,
  "head-and-shoulders": <path d="M1.5 13l3-4 2 3 2.5-8 2.5 8 2-3 3 4" />,
  "elliott-impulse": <><path d="M2 15l3-6 2 3 4-9 2 4 3-3" />{dot(2, 15)}</>,
  "cyclic-lines": <path d="M2 3v12M7 3v12M12 3v12M17 3v12" strokeDasharray="1.5 1.5" />,
  "sine-line": <path d="M1.5 9c2-6 4-6 6 0s4 6 6 0 2-4 3-3" />,
  "long-position": <><rect x="3" y="2.5" width="12" height="6" /><rect x="3" y="8.5" width="12" height="7" strokeDasharray="1.5 1.5" /><path d="M9 7V4M7.5 5.2L9 3.8l1.5 1.4" /></>,
  "short-position": <><rect x="3" y="2.5" width="12" height="7" strokeDasharray="1.5 1.5" /><rect x="3" y="9.5" width="12" height="6" /><path d="M9 11v3M7.5 12.8L9 14.2l1.5-1.4" /></>,
  "anchored-vwap": <><path d="M2 14c4-1 6-6 14-10" />{dot(2, 14)}</>,
  "fixed-range-volume-profile": <path d="M3 3v12M3 4h5M3 7h9M3 10h12M3 13h7" />,
  "price-range": <><path d="M3 3h12M3 15h12M9 4v10M7 6l2-2 2 2M7 12l2 2 2-2" /></>,
  "date-range": <><path d="M3 3v12M15 3v12M4 9h10M6 7L4 9l2 2M12 7l2 2-2 2" /></>,
  "date-and-price-range": <><rect x="2.5" y="2.5" width="13" height="13" /><path d="M9 4v10M4 9h10" /></>,
  brush: <path d="M3 15c3 0 3-3 5-4l7-8M8 11l1.5 1.5" />,
  highlighter: <path d="M4 14l-1 2h4l1-1M5 13l8-9 2 2-8 9z" />,
  "arrow-marker": <path d="M3 15l9-9M8 3.5h6.5V10" />,
  arrow: <>{dot(3, 15)}<path d="M4.2 13.8L15 3M10 3h5v5" /></>,
  "arrow-mark-up": <path d="M9 2l6 7h-3.5v7h-5V9H3z" />,
  "arrow-mark-down": <path d="M9 16l6-7h-3.5V2h-5v7H3z" />,
  rectangle: <><rect x="3" y="4" width="12" height="10" />{dot(3, 4)}{dot(15, 14)}</>,
  "rotated-rectangle": <path d="M9 2l7 7-7 7-7-7z" />,
  path: <><path d="M2 14l4-6 4 4 6-8" /><path d="M13 4h3v3" /></>,
  circle: <><circle cx="9" cy="9" r="6.5" />{dot(9, 9)}</>,
  ellipse: <ellipse cx="9" cy="9" rx="7.5" ry="4.5" />,
  polyline: <><path d="M2 13l4-8 5 9 5-6" />{dot(2, 13)}{dot(6, 5)}{dot(11, 14)}{dot(16, 8)}</>,
  triangle: <path d="M3 15L9 3l6 12z" />,
  arc: <path d="M2 14C3 5 15 5 16 14" />,
  curve: <path d="M2 15C5 2 13 16 16 3" />,
  "double-curve": <path d="M2 9c2-6 5-6 7 0s5 6 7 0" />,
  text: <path d="M4 4h10M9 4v11M7 15h4" />,
  note: <><path d="M3 3h12v8l-4 4H3z" /><path d="M11 15v-4h4" /></>,
  "price-note": <><path d="M2 9h5" />{dot(8, 9)}<rect x="10" y="5" width="6" height="8" rx="1" /></>,
  pin: <path d="M9 16v-5M5 11h8l-1.5-3V3h-5v5z" />,
  table: <><rect x="2.5" y="3.5" width="13" height="11" /><path d="M2.5 7.5h13M2.5 11h13M7 3.5v11" /></>,
  callout: <path d="M3 3h12v8H8l-4 4v-4H3z" />,
  comment: <path d="M3 4h12v8H9l-3 3v-3H3z" />,
  "price-label": <path d="M2 9l4-4h10v8H6z" />,
  signpost: <path d="M9 16V7M4 3h9l2 2-2 2H4z" />,
  "flag-mark": <path d="M4 16V2M4 3h10l-2 3 2 3H4" />,
  image: <><rect x="2.5" y="3.5" width="13" height="11" rx="1" /><path d="M3 13l4-4 3 3 2-2 3 3" /><circle cx="12" cy="7" r="1.2" /></>
}

/** A family's icon, for its tools without one of their own. */
const FAMILY: ReadonlyArray<readonly [ReadonlyArray<DrawingKind>, DrawingKind]> = [
  [["flat-top-bottom", "disjoint-channel"], "parallel-channel"],
  [["schiff-pitchfork", "modified-schiff-pitchfork", "inside-pitchfork", "pitchfan"], "pitchfork"],
  [
    [
      "trend-based-fib-extension",
      "fib-channel",
      "fib-time-zone",
      "fib-speed-resistance-fan",
      "trend-based-fib-time",
      "fib-circles",
      "fib-spiral",
      "fib-speed-resistance-arcs",
      "fib-wedge"
    ],
    "fib-retracement"
  ],
  [["gann-square-fixed", "gann-square"], "gann-box"],
  [["cypher-pattern", "abcd-pattern", "triangle-pattern", "three-drives-pattern"], "xabcd-pattern"],
  [["elliott-correction", "elliott-triangle", "elliott-double-combo", "elliott-triple-combo"], "elliott-impulse"],
  [["time-cycles"], "cyclic-lines"],
  [["position-forecast", "bar-pattern", "ghost-feed", "sector"], "trend-angle"],
  [["anchored-volume-profile"], "fixed-range-volume-profile"]
]
const familyOf = new Map<DrawingKind, DrawingKind>(FAMILY.flatMap(([kinds, icon]) => kinds.map((kind) => [kind, icon] as const)))

export function ToolIcon({ kind }: { readonly kind: DrawingKind }) {
  return <Svg>{ICONS[kind] ?? ICONS[familyOf.get(kind) ?? "trend-line"]}</Svg>
}
